import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const ChevronRightIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const PhaseCard = ({ href, title, description, phase }) => (
  <a
    href={href}
    className="group block rounded-3xl border border-gray-100 bg-white p-8 shadow-soft hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
  >
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue group-hover:bg-school-blue group-hover:text-white transition-colors duration-300 shadow-sm">
        <span className="font-heading font-bold">{phase}</span>
      </div>
      <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-600 group-hover:bg-school-blue group-hover:text-white transition-colors duration-300">
        <ChevronRightIcon />
      </div>
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 group-hover:text-school-gold transition-colors mb-3 tracking-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7">
      {description}
    </p>
  </a>
);

// ==================== MAIN NYSED GRANT COMPONENT ====================

const NYSEDGrant2025 = () => {
  const phases = [
    {
      phase: "1",
      title: "Interactive Rainforest Experience",
      description: "Students showcase learning through interactive experiences with QR codes and digital publications.",
      href: "/ltg-phase-1"
    },
    {
      phase: "2",
      title: "PS 85 News & Videos",
      description: "Participating classes create news content and video productions showcasing student work.",
      href: "/ltg-phase-2"
    },
    {
      phase: "3",
      title: "Podcasts & Digital Media",
      description: "Students explore podcasting, video production, and digital storytelling.",
      href: "/ltg-phase-3"
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-green-400" aria-hidden="true" />
                Learning Technology Grant 2025
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white tracking-tight leading-[1.02]">
                NYSED CSD 10 Learning Technology Grant
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Empowering learning through innovative technology initiatives
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== ABOUT THE GRANT ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <SectionHeader
                  eyebrow="Our Initiative"
                  title="About the Grant"
                  description="Transforming education through technology"
                />
                <div className="mt-8 space-y-6 text-base md:text-lg text-gray-600 leading-relaxed">
                  <p>
                    PS 85 is proud to participate in the NYSED Community School District 10 Learning Technology Grant for 2025. This grant enables us to integrate cutting-edge technology into our curriculum, providing students with modern tools and resources to enhance their educational experience.
                  </p>
                  <p>
                    Through this initiative, our students develop digital literacy, creative expression, and 21st-century skills that will prepare them for future success.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-school-blue/8 text-school-blue shadow-soft">
                    <AcademicCapIcon className="w-12 h-12" />
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-school-blue">3</div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider mt-1">Phases</div>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-4">
                  <div className="inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-school-blue/8 text-school-blue shadow-soft">
                    <SparklesIcon className="w-12 h-12" />
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-school-blue">100+</div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider mt-1">Students</div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== PROGRAM PHASES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Explore Phases"
              title="Program Phases"
              description="Discover how each phase of our Learning Technology Grant brings innovative learning experiences to our students."
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              {phases.map((phase, index) => (
                <PhaseCard key={index} {...phase} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Explore Our Programs
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Discover how our Learning Technology Grant initiatives are transforming education at PS 85.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/ltg-phase-1"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                View Phase 1
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default NYSEDGrant2025;
